import React, { useState, useEffect, useCallback } from "react";
import { API } from "../../App";
import { toast } from "sonner";
import axios from "axios";
import {
  DollarSign, User, Phone, Mail, MapPin, Clock, X,
  ChevronRight, Trash2, CheckCircle, AlertCircle, Briefcase,
  FileText, Search
} from "lucide-react";

const STATUS_OPTIONS = ["New", "Contacted", "Scheduled", "Closed"];
const STATUS_STYLES = {
  New: "bg-blue-500/20 text-blue-400",
  Contacted: "bg-yellow-500/20 text-yellow-400",
  Scheduled: "bg-green-500/20 text-green-400",
  Closed: "bg-[#A0AAB2]/20 text-[#A0AAB2]",
};

const FinancingLeadsTab = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchApps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/financing-applications`, { withCredentials: true });
      setApplications(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  // Poll for new submissions every 15 seconds
  useEffect(() => {
    const interval = setInterval(fetchApps, 15000);
    return () => clearInterval(interval);
  }, [fetchApps]);

  const handleStatusUpdate = async (appId, newStatus) => {
    try {
      await axios.patch(`${API}/financing-applications/${appId}`, { status: newStatus }, { withCredentials: true });
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: newStatus } : a));
      if (selectedApp?.id === appId) setSelectedApp(p => ({ ...p, status: newStatus }));
      toast.success(`Status updated to ${newStatus}`);
    } catch (e) { toast.error("Failed to update status"); }
  };

  const handleDelete = async (appId) => {
    if (!window.confirm("Delete this financing application?")) return;
    try {
      await axios.delete(`${API}/financing-applications/${appId}`, { withCredentials: true });
      setApplications(prev => prev.filter(a => a.id !== appId));
      if (selectedApp?.id === appId) setSelectedApp(null);
      toast.success("Application deleted");
    } catch (e) { toast.error("Failed to delete"); }
  };

  const filtered = applications.filter(a => {
    const matchSearch = !searchTerm || a.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.email.toLowerCase().includes(searchTerm.toLowerCase()) || a.phone.includes(searchTerm);
    const matchStatus = filterStatus === "all" || a.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-4" data-testid="financing-leads-tab">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Financing Leads ({applications.length})</h2>
          <p className="text-[#A0AAB2] text-sm">Credit applications from the financing page</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search..." className="bg-[#1F2027] border border-[#2A2B32] rounded-lg pl-9 pr-4 py-2 text-white text-sm w-48"
              data-testid="financing-search" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="bg-[#1F2027] border border-[#2A2B32] rounded-lg px-3 py-2 text-white text-sm"
            data-testid="financing-filter">
            <option value="all">All Status</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF6B35]"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={40} className="mx-auto text-[#2A2B32] mb-3" />
            <p className="text-[#A0AAB2]">{applications.length === 0 ? "No financing applications yet" : "No results match your search"}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(app => (
              <div key={app.id} onClick={() => setSelectedApp(app)}
                className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-4 cursor-pointer hover:border-[#FF6B35]/50 transition-all"
                data-testid={`financing-app-${app.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-white font-semibold">{app.full_name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[app.status] || STATUS_STYLES.New}`}>
                        {app.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-[#A0AAB2] text-xs">
                      <span className="flex items-center space-x-1"><Phone size={10} /><span>{app.phone}</span></span>
                      <span className="flex items-center space-x-1"><Mail size={10} /><span>{app.email}</span></span>
                      <span className="flex items-center space-x-1"><DollarSign size={10} /><span>${app.loan_amount}</span></span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-[#A0AAB2] text-xs">{new Date(app.created_at).toLocaleDateString()}</span>
                    <span className="text-[#FF6B35] font-bold">${Number(app.loan_amount).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedApp && (
        <FinancingDetailModal app={selectedApp} onClose={() => setSelectedApp(null)}
          onStatusUpdate={handleStatusUpdate} onDelete={handleDelete} />
      )}
    </div>
  );
};

const FinancingDetailModal = ({ app, onClose, onStatusUpdate, onDelete }) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="financing-detail-modal">
    <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-lg p-6 my-8 max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white">{app.full_name}</h3>
          <p className="text-[#A0AAB2] text-sm">Submitted {new Date(app.created_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={() => onDelete(app.id)} className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30" data-testid="delete-financing-app">
            <Trash2 size={14} />
          </button>
          <button onClick={onClose} className="p-2 text-[#A0AAB2] hover:text-white"><X size={20} /></button>
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center space-x-2 mb-6">
        <span className="text-[#A0AAB2] text-sm">Status:</span>
        {STATUS_OPTIONS.map(s => (
          <button key={s} onClick={() => onStatusUpdate(app.id, s)}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
              app.status === s ? STATUS_STYLES[s] + " border border-current" : "bg-[#2A2B32] text-[#A0AAB2]"
            }`} data-testid={`status-btn-${s}`}>
            {s}
          </button>
        ))}
      </div>

      {/* Loan Info */}
      <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-xl p-4 mb-4 text-center">
        <p className="text-[#A0AAB2] text-xs mb-1">Loan Amount Requested</p>
        <p className="text-3xl font-bold text-[#FF6B35]">${Number(app.loan_amount).toLocaleString()}</p>
        {app.loan_purpose && <p className="text-[#A0AAB2] text-sm mt-1">{app.loan_purpose}</p>}
      </div>

      {/* Personal Info */}
      <div className="bg-[#1F2027] rounded-lg p-4 mb-4">
        <h4 className="text-white font-semibold text-sm mb-3 flex items-center space-x-2">
          <User size={14} className="text-[#FF6B35]" /><span>Personal Information</span>
        </h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <DetailRow label="Phone" value={app.phone} />
          <DetailRow label="Email" value={app.email} />
          <DetailRow label="Address" value={`${app.address}, ${app.city}, ${app.state} ${app.zip_code}`} span />
          <DetailRow label="Date of Birth" value={app.dob} />
          <DetailRow label="SSN (last 4)" value={`***-**-${app.last4_ssn}`} />
        </div>
      </div>

      {/* Financial Info */}
      <div className="bg-[#1F2027] rounded-lg p-4 mb-4">
        <h4 className="text-white font-semibold text-sm mb-3 flex items-center space-x-2">
          <DollarSign size={14} className="text-[#FF6B35]" /><span>Financial Information</span>
        </h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <DetailRow label="Monthly Income" value={`$${app.monthly_income}`} />
          <DetailRow label="Employment" value={app.employment_status} />
          <DetailRow label="Residence" value={app.residence} />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex space-x-3">
        <a href={`tel:${app.phone}`} className="flex-1 flex items-center justify-center space-x-2 py-3 bg-green-500/20 text-green-400 rounded-lg font-medium text-sm hover:bg-green-500/30">
          <Phone size={14} /><span>Call</span>
        </a>
        <a href={`mailto:${app.email}`} className="flex-1 flex items-center justify-center space-x-2 py-3 bg-blue-500/20 text-blue-400 rounded-lg font-medium text-sm hover:bg-blue-500/30">
          <Mail size={14} /><span>Email</span>
        </a>
      </div>
    </div>
  </div>
);

const DetailRow = ({ label, value, span }) => (
  <div className={span ? "col-span-2" : ""}>
    <p className="text-[#A0AAB2] text-xs">{label}</p>
    <p className="text-white font-medium">{value}</p>
  </div>
);

export default FinancingLeadsTab;
